// src/components/molecules/NavBar.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../atoms/Logo';
import LinkButton from '../atoms/LinkButton';
import { activeOffer } from '../../data/activeOffer';

const navLinkBase =
  'text-yellow hover:text-white transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-navy rounded-sm motion-reduce:transition-none';

const navLinkActive = 'text-white underline decoration-yellow decoration-2 underline-offset-4';

const isRouteActive = (pathname, to) => {
  if (to === '/') {
    return pathname === '/';
  }
  return pathname === to;
};

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const menuToggleRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    if (!mediaQuery?.addEventListener) {
      return undefined;
    }

    const handleResize = (event) => {
      if (event.matches) {
        closeMenu();
      }
    };

    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, [closeMenu]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        menuToggleRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, closeMenu]);

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const navItems = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/footylab', label: 'footyLab' },
    { to: '/camp', label: 'Camps' },
    { to: '/partners', label: 'Partners' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact Us' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-navy shadow-md transition-all duration-300 motion-reduce:transition-none ${
        isScrolled ? 'bg-opacity-90 backdrop-blur-sm' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link
            to="/"
            className="flex-shrink-0 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
          >
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center space-x-6" aria-label="Main navigation">
            {navItems.map(({ to, label }) => (
              <NavLink key={to} to={to} pathname={location.pathname}>
                {label}
              </NavLink>
            ))}
            <LinkButton
              to={activeOffer.destination}
              variant="primary"
              className="text-sm py-2 px-4 whitespace-nowrap"
            >
              {activeOffer.label}
            </LinkButton>
          </nav>
          <button
            ref={menuToggleRef}
            type="button"
            className={`md:hidden text-yellow ${navLinkBase}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="site-menu"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
      <div id="site-menu" className={`md:hidden ${isMenuOpen ? '' : 'hidden'}`}>
        <nav className="px-2 pt-2 pb-3 space-y-1 sm:px-3" aria-label="Mobile navigation">
          {navItems.map(({ to, label }) => (
            <MobileNavLink
              key={to}
              to={to}
              pathname={location.pathname}
              onClick={closeMenu}
            >
              {label}
            </MobileNavLink>
          ))}
          <div className="px-3 pt-2">
            <LinkButton
              to={activeOffer.destination}
              variant="primary"
              className="w-full text-center"
              onClick={closeMenu}
            >
              {activeOffer.label}
            </LinkButton>
          </div>
        </nav>
      </div>
    </header>
  );
};

const NavLink = ({ to, pathname, children }) => {
  const active = isRouteActive(pathname, to);
  return (
    <Link
      to={to}
      className={`${navLinkBase} ${active ? navLinkActive : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </Link>
  );
};

const MobileNavLink = ({ to, pathname, children, onClick }) => {
  const active = isRouteActive(pathname, to);
  return (
    <Link
      to={to}
      className={`block px-3 py-2 rounded-sm ${navLinkBase} ${active ? navLinkActive : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

export default NavBar;
